import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import '../styles/Login.css';

function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastResetTime, setLastResetTime] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  const handlePasswordReset = async () => {
    // Rate limit - 1 reset per 60 seconds
    if (Date.now() - lastResetTime < 60000) {
      setError('Please wait 60 seconds before requesting another reset.');
      return;
    }

    if (!email) {
      setError('Please enter your email address first');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setLastResetTime(Date.now());
      setMessage('Password reset email sent! Check your inbox.');
      setError('');
    } catch (err) {
      console.error('Password reset error:', err);
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email address');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many reset attempts. Please try again later.');
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Check if email is verified
      if (!user.emailVerified) {
        setError('Please verify your email before logging in. Check your inbox for the verification link.');
        
        // Offer to resend verification email
        if (window.confirm('Email not verified. Would you like us to resend the verification email?')) {
          try {
            await sendEmailVerification(user);
            alert('Verification email sent! Please check your inbox.');
          } catch (err) {
            console.error('Error sending verification email:', err);
          }
        }
        
        await auth.signOut();
        setLoading(false);
        return;
      }

      // Get the user's ID token to check custom claims
      const idTokenResult = await user.getIdTokenResult();

      // Check if user has admin custom claims
      if (!idTokenResult.claims.isAdmin) {
        setError('Access denied. This account does not have admin privileges. Please use the mobile app instead.');
        await auth.signOut();
        setLoading(false);
        return;
      }

      // Verify admin document exists (backup check)
      const adminDoc = await getDoc(doc(db, 'admins', user.uid));
      if (!adminDoc.exists()) {
        setError('Admin account not found. Please contact support.');
        await auth.signOut();
        setLoading(false);
        return;
      }

      // Check if admin has a valid company
      const companyId = idTokenResult.claims.companyId;
      if (!companyId || companyId === 'Null') {
        setError('Your admin account is not associated with a company. Please contact support.');
        await auth.signOut();
        setLoading(false);
        return;
      }

      // All checks passed - navigate to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('Invalid email or password');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed login attempts. Please try again later.');
      } else if (err.code === 'auth/invalid-credential') {
        setError('Invalid email or password');
      } else {
        setError('Failed to log in. Please try again.');
      }
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>Zella</h1>
          <h2>Admin Login</h2>
          <p>Access your dashboard</p>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        {message && <div className="success-message">{message}</div>}
        
        <form className="login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@company.com..."
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password..."
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
          </div>
          
          <button type="submit" className="btn-login-submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Log In'}
          </button>
          
          <div className="login-footer">
            <button 
              type="button"
              onClick={handlePasswordReset}
              className="forgot-password-btn"
            >
              Forgot password?
            </button>
          </div>

          <div className="signup-link">
            <p>Don't have an account? <a href="/signup">Sign Up</a></p>
          </div>
        </form>
        
        <div className="back-to-home">
          <a href="/">← Back to home</a>
        </div>
      </div>
    </div>
  );
}

export default Login;